import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot, UrlTree } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { MenuItemService } from '../services/menu-item.service';
import { StorageUtil } from '../utils/storage.util';

@Injectable({
  providedIn: 'root'
})
export class MenuItemGuard implements CanActivate, CanActivateChild {
  private userId: number;
  private marinaId: number;
  private allowedRouterLinks: string[] = [];
  private allMenuRouterLinks: string[] = [];

  /**
   * Rotas sempre liberadas, independentemente de role/features. Inclui a rota
   * de aterrissagem pós-login: se o conjunto de features de uma marina nao
   * cobrir o dashboard, o usuario NUNCA pode ficar preso na tela de login.
   */
  private static readonly ALWAYS_ALLOWED: string[] = [
    '/app/dashboard',
    '/app/module-disabled',
  ];

  /** Limite para as chamadas de acesso ao menu nao prenderem a navegacao. */
  private static readonly ACCESS_TIMEOUT_MS = 8000;

  constructor(private menuItemService: MenuItemService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.check(state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.check(state);
  }

  private check(state: RouterStateSnapshot): Observable<boolean> | boolean {
    const userId = StorageUtil.getUserId();
    const marinaId = StorageUtil.getMarinaId();
    const stateUrl = this.normalizeUrl(state.url);

    // Sem sessao resolvida: nao trancar aqui. O AuthGuard (canActivate da rota
    // 'app') ja redireciona para o login quando nao ha autenticacao.
    if (!userId || !marinaId) {
      return true;
    }

    if (userId !== this.userId || marinaId !== this.marinaId) {
      return forkJoin({
        allowed: this.menuItemService.findRouterLinkAccessByUser(),
        universe: this.menuItemService.findAllMenuRouterLinks(),
      }).pipe(
        timeout(MenuItemGuard.ACCESS_TIMEOUT_MS),
        map(({ allowed, universe }) => {
          this.userId = userId;
          this.marinaId = marinaId;
          this.allowedRouterLinks = allowed || [];
          this.allMenuRouterLinks = universe || [];
          return this.isOk(stateUrl);
        }),
        // Falha/timeout ao carregar os acessos: fail-open para nao prender o
        // usuario no login. A seguranca real continua sendo aplicada pelo
        // backend em cada endpoint; aqui e apenas roteamento de UI. Nao grava
        // userId/marinaId para que a proxima navegacao tente recarregar.
        catchError(() => of(true))
      );
    }

    return this.isOk(stateUrl);
  }

  private normalizeUrl(url: string): string {
    let stateUrl = url;
    if (stateUrl.indexOf('?') !== -1) {
      stateUrl = stateUrl.substring(0, stateUrl.indexOf('?'));
    }
    if (stateUrl.indexOf('#') !== -1) {
      stateUrl = stateUrl.substring(0, stateUrl.indexOf('#'));
    }
    return stateUrl;
  }

  private isOk(path: string): boolean {
    // Rota sempre liberada (aterrissagem/fallback) -> nunca trancar
    if (this.isAlwaysAllowed(path)) {
      return true;
    }
    // Universo nao carregado -> nao trancar (fail-open)
    if (!this.allMenuRouterLinks || this.allMenuRouterLinks.length === 0) {
      return true;
    }
    // Rota fora do universo de menu_item -> rota interna/transito, libera
    if (!this.matchesAny(this.allMenuRouterLinks, path)) {
      return true;
    }
    // Rota e do menu -> exige estar na lista de permitidas do usuario
    return this.matchesAny(this.allowedRouterLinks, path);
  }

  private isAlwaysAllowed(path: string): boolean {
    return MenuItemGuard.ALWAYS_ALLOWED.some(route => this.routesMatch(route, path));
  }

  private matchesAny(items: string[], path: string): boolean {
    if (!items || items.length === 0) return false;
    return items.some(item => this.routesMatch(item, path));
  }

  /**
   * Compara rota cadastrada com URL acessada normalizando prefixos.
   * Entries em menu_item costumam vir como '#/app/...' enquanto a URL
   * navegada vem como '/app/...'.
   */
  private routesMatch(menuRoute: string, stateUrl: string): boolean {
    if (!menuRoute) return false;
    return this.stripPrefix(menuRoute) === this.stripPrefix(stateUrl);
  }

  private stripPrefix(value: string): string {
    let v = (value || '').trim();
    if (v.startsWith('#')) v = v.substring(1);
    if (!v.startsWith('/')) v = '/' + v;
    if (v.length > 1 && v.endsWith('/')) v = v.substring(0, v.length - 1);
    return v.toLowerCase();
  }
}
